/**
 * New Relic Browser Agent Integration
 *
 * This module provides logging functions that integrate with New Relic's Browser Agent API.
 * The New Relic Browser Agent is automatically loaded via script tag in index.html.
 * This module allows you to manually call New Relic functions from your TypeScript code.
 *
 * The Browser Agent API is available via window.newrelic once the script loads.
 */

import type { LogContext } from "../api/types.js";

// Type declaration for New Relic Browser Agent API
interface NewRelicLogOptions {
  customAttributes?: Record<string, any>;
  level?: "debug" | "error" | "info" | "trace" | "warn";
}

interface NewRelicBrowserAPI {
  log(message: string, options?: NewRelicLogOptions): void;
  noticeError(error: Error, customAttributes?: Record<string, any>): void;
  recordCustomEvent(eventType: string, attributes?: Record<string, any>): void;
  setCustomAttribute(key: string, value: string | number | boolean): void;
  setPageViewName(name: string, host?: string): void;
  setUserId(userId: string): void;
  addPageAction(name: string, attributes?: Record<string, any>): void;
}

/**
 * Get the New Relic Browser Agent API
 * Returns null if the agent is not loaded yet
 */
function getNewRelic(): NewRelicBrowserAPI | null {
  if (typeof window === "undefined") {
    return null;
  }

  // Check if window.newrelic exists (Browser Agent API)
  const nr = (window as any).newrelic;
  if (nr && typeof nr.log === "function") {
    return nr as NewRelicBrowserAPI;
  }

  return null;
}

/**
 * Initialize New Relic handler
 * This is a no-op for Browser Agent - it's already initialized via script tag
 * Kept for compatibility with log-handler.ts
 */
export async function initializeNewRelic(): Promise<void> {
  // Browser Agent is initialized automatically via script tag
  // This function exists for compatibility but does nothing
  const nr = getNewRelic();
  if (nr) {
    // Optional: You could set initial attributes here if needed
    // nr.setCustomAttribute('application', 'islr-webapp');
  }
}

/**
 * Log an informational message
 */
export function logInfo(message: string, operationName: string, context?: Partial<LogContext>, properties?: Record<string, any>) {
  const nr = getNewRelic();
  if (!nr) {
    return;
  }

  try {
    const allProperties = {
      ...sanitizeProperties(properties),
      ...sanitizeProperties(context),
      operationName,
    };

    // Use log() with options object containing customAttributes and level
    nr.log(message, {
      customAttributes: allProperties,
      level: "info",
    });
  } catch (error) {
    console.error("Error logging info:", error instanceof Error ? error.message : "Unknown error");
  }
}

/**
 * Log a warning message
 */
export function logWarning(message: string, operationName: string, context?: Partial<LogContext>, properties?: Record<string, any>) {
  const nr = getNewRelic();
  if (!nr) {
    return;
  }

  try {
    const allProperties = {
      ...sanitizeProperties(properties),
      ...sanitizeProperties(context),
      operationName,
    };

    // Use log() with options object containing customAttributes and level
    nr.log(message, {
      customAttributes: allProperties,
      level: "warn",
    });
  } catch (error) {
    console.error("Error logging warning:", error instanceof Error ? error.message : "Unknown error");
  }
}

/**
 * Log an error
 */
export function logError(error: Error, operationName: string, context?: Partial<LogContext>, properties?: Record<string, any>) {
  const nr = getNewRelic();
  if (!nr) {
    return;
  }

  try {
    const allProperties = {
      ...sanitizeProperties(properties),
      ...sanitizeProperties(context),
      operationName,
    };

    // Use noticeError() for errors in Browser Agent
    nr.noticeError(error, allProperties);
  } catch (err) {
    console.warn("[newrelic-handler] Error logging error:", err instanceof Error ? err.message : "Unknown error");
  }
}

/**
 * Log a metric
 * Note: Browser Agent doesn't have a direct metric API, so we use custom events
 */
export function logMetric(name: string, value: number, context?: Partial<LogContext>, properties?: Record<string, any>) {
  const nr = getNewRelic();
  if (!nr) {
    return;
  }

  try {
    const allProperties = {
      ...sanitizeProperties(properties),
      ...sanitizeProperties(context),
      metricName: name,
      metricValue: value,
    };

    // Use recordCustomEvent for metrics (Browser Agent doesn't have recordMetric)
    nr.recordCustomEvent("Metric", allProperties);
  } catch (error) {
    console.warn("[newrelic-handler] Error logging metric:", error instanceof Error ? error.message : "Unknown error");
  }
}

/**
 * Track a page view
 */
export function trackPageView(name: string, context?: Partial<LogContext>, properties?: Record<string, any>) {
  const nr = getNewRelic();
  if (!nr) {
    return;
  }

  try {
    // Use setPageViewName for page views
    nr.setPageViewName(name);

    // Also record as custom event with additional properties
    if (properties || context) {
      const allProperties = {
        ...sanitizeProperties(properties),
        ...sanitizeProperties(context),
        pageName: name,
      };
      nr.recordCustomEvent("PageView", allProperties);
    }
  } catch (error) {
    console.warn("Error tracking page view:", error instanceof Error ? error.message : "Unknown error");
  }
}

/**
 * Track a custom event
 */
export function trackEvent(name: string, context?: Partial<LogContext>, properties?: Record<string, any>) {
  const nr = getNewRelic();
  if (!nr) {
    return;
  }

  try {
    const allProperties = {
      ...sanitizeProperties(properties),
      ...sanitizeProperties(context),
    };

    nr.recordCustomEvent(name, allProperties);
  } catch (error) {
    console.warn("Error tracking event:", error instanceof Error ? error.message : "Unknown error");
  }
}

// --- helpers ---

function clampString(value: string, maxLen: number): string {
  if (typeof value !== "string") return String(value ?? "");
  return value.length > maxLen ? value.slice(0, maxLen) : value;
}

function sanitizeProperties(input?: Record<string, any>): Record<string, any> | undefined {
  if (!input || typeof input !== "object") return input;
  const MAX_STRING = 4096;
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined) continue;
    const key = String(k).slice(0, 150);
    const valType = typeof v;
    if (valType === "string") {
      out[key] = clampString(v as string, MAX_STRING);
    } else if (valType === "number" || valType === "boolean") {
      out[key] = v;
    } else if (v === null) {
      out[key] = null;
    } else {
      // Objects, arrays, functions, symbols -> stringify safely
      try {
        out[key] = clampString(JSON.stringify(v, safeReplacer(), 0), MAX_STRING);
      } catch (_e) {
        out[key] = "[unserializable]";
      }
    }
  }
  return out;
}

function safeReplacer() {
  const seen = new WeakSet();
  return (_key: string, value: any) => {
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) return "[circular]";
      seen.add(value);
    }
    if (typeof value === "function") return "[function]";
    if (typeof value === "symbol") return String(value);
    return value;
  };
}
